import type { JSX } from "react";
import { useEffect, useState } from "react";

import type { LandingCity } from "@/lib/api";
import { type LivePosts, loadLivePosts } from "@/lib/live-posts";

/** The posts the panel is showing, tagged with the slug they came from (see below). */
interface LoadedPosts {
  slug: string;
  posts: LivePosts | null;
}

/**
 * The panel the home page's directory raises: the city's latest posts from its own guide.
 *
 * Raised by the row, not by the planet: `MapSection` hands it the city the reader's pointer is on in
 * the left-hand list — the same selection whose routes and aircraft the globe is then drawing — and
 * takes it away when the pointer leaves that row. Nothing here is tied to a glyph on the map.
 *
 * Written imperatively nowhere — this is a React component, because unlike the pointer's own cards it
 * holds no per-frame geometry: it is one fixed corner of the stage, and the two facts it draws (which
 * city's row the reader is on, and that city's posts) are both state.
 *
 * A city with no published guide has no posts to read and draws nothing at all, so the panel never
 * promises a page that does not exist. The covers are that guide's own published first pages, each
 * one a plain link to the post — the same posts, and the same exception, the guide itself shows.
 */
export function CityPostsPanel({ city }: { city: LandingCity | null }): JSX.Element | null {
  // The loaded posts, tagged with the slug they came from: hovering a second city must not show the
  // first one's covers while its own file is still in flight.
  const [loaded, setLoaded] = useState<LoadedPosts | null>(null);
  const slug = city !== null && city.cityPagePublished ? city.slug : null;
  useEffect(() => {
    if (slug === null) return undefined;
    let live = true;
    void (async () => {
      const next = await loadLivePosts(slug);
      if (live) setLoaded({ slug, posts: next });
    })();
    return () => {
      live = false;
    };
  }, [slug]);
  if (city === null || slug === null) return null;
  const posts = loaded !== null && loaded.slug === slug ? loaded.posts : null;
  const handle = posts?.handle ?? city.instagramHandle;
  // Nothing to follow is nothing to open: the panel exists to send a reader to the account.
  if (posts === null || handle === null) return null;
  return (
    <aside className="atlas-city-posts" aria-label={`Latest posts from ${handle}`}>
      <p className="atlas-city-posts-kicker">LATEST FROM @{handle.toUpperCase()}</p>
      <ul className="atlas-city-posts-row">
        {posts.posts.map((post) => (
          <li key={post.position}>
            <a
              className="atlas-city-post"
              href={post.permalink}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${city.name} Instagram post`}
            >
              <img
                src={post.image}
                srcSet={post.imageSrcSet ?? undefined}
                sizes="64px"
                alt=""
                loading="lazy"
                decoding="async"
              />
            </a>
          </li>
        ))}
      </ul>
      <a
        className="atlas-city-posts-cta"
        href={`https://instagram.com/${handle}`}
        target="_blank"
        rel="noreferrer"
      >
        FOLLOW US ON INSTAGRAM →
      </a>
    </aside>
  );
}
